import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip } from '@mui/material';
import { PlayArrow, Pause, Add, Edit, Delete, PlayCircle, FastRewind, FastForward, ZoomIn, ZoomOut } from '@mui/icons-material';
import { AuditioBackendManager, SnippetData } from './AuditioBackend';
import YouTubePlayer from './YouTubePlayer';
import YouTubeTimeline from './YouTubeTimeline';
import UnifiedSnippetsView from './UnifiedSnippetsView';
import PlaybackSpeedDropdown from './components/PlaybackSpeedDropdown';
import { useAuth } from './contexts/AuthContext';
import { metricsService } from './services/MetricsService';

interface YouTubeVideoViewerProps {
  auditioBackendManager: AuditioBackendManager;
  audioFileId: number;
  youtubeUrl: string;
  fileName: string;
  snippets: SnippetData[];
  onSnippetsChange: () => void;
}

const YouTubeVideoViewer: React.FC<YouTubeVideoViewerProps> = ({
  auditioBackendManager,
  audioFileId,
  youtubeUrl,
  fileName,
  snippets,
  onSnippetsChange
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [backendSnippets, setBackendSnippets] = useState<SnippetData[]>([]);
  const [activeSnippet, setActiveSnippet] = useState<SnippetData | null>(null);
  const [loopMode, setLoopMode] = useState<'none' | 'snippet' | 'track'>('none');
  const [startMarker, setStartMarker] = useState<number | null>(null);
  const [endMarker, setEndMarker] = useState<number | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [ownerId, setOwnerId] = useState<number | null | undefined>(undefined);
  const { user, firebaseUser } = useAuth();
  const playerRef = useRef<any>(null);
  const lastLoopTrackedRef = useRef<{ snippetId: number | null, time: number }>({ snippetId: null, time: 0 });
  
  // Zoom state for timeline
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [viewportWidth, setViewportWidth] = useState<number>(0);
  const isZoomInitializedRef = useRef<boolean>(false);
  
  // Initialize zoom level when duration and container are available
  useEffect(() => {
    if (timelineContainerRef.current && duration > 0 && !isZoomInitializedRef.current) {
      const width = timelineContainerRef.current.clientWidth;
      if (width > 0) {
        const baseZoom = width / duration;
        setZoomLevel(baseZoom);
        setViewportWidth(width);
        isZoomInitializedRef.current = true;
      }
    }
  }, [duration]);
  
  // Update viewport width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (timelineContainerRef.current) {
        const width = timelineContainerRef.current.clientWidth;
        setViewportWidth(width);
      }
    };
    
    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (timelineContainerRef.current) {
      resizeObserver.observe(timelineContainerRef.current);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  
  // Check if current user is the owner or if file has no owner (always editable)
  // Files with no owner (ownerId === null) are always editable by everyone
  // If ownerId is undefined, we don't know yet, so default to false (restrictive) until we know
  // Use Number() to ensure type-safe comparison (user?.id might be string or number)
  const isOwner = ownerId === null
      ? true  // No owner - everyone can edit
      : ownerId === undefined
      ? false  // Unknown - be restrictive until we know
      : Number(user?.id) === Number(ownerId);  // Check if current user is the owner (type-safe comparison)
  
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);
  
  // Refs to store current values for event handlers
  const currentTimeRef = useRef(currentTime);
  const durationRef = useRef(duration);
  const backendSnippetsRef = useRef(backendSnippets);
  const startMarkerRef = useRef(startMarker);
  const endMarkerRef = useRef(endMarker);
  
  // Update refs when values change
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);
  
  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);
  
  useEffect(() => {
    backendSnippetsRef.current = backendSnippets;
  }, [backendSnippets]);
  
  useEffect(() => {
    startMarkerRef.current = startMarker;
  }, [startMarker]);
  
  useEffect(() => {
    endMarkerRef.current = endMarker;
  }, [endMarker]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent shortcuts when typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case ' ':
          event.preventDefault();
          handlePlayPause();
          break;
        case 'z':
          event.preventDefault();
          handleSeek(Math.max(0, currentTimeRef.current - 30));
          break;
        case 'x':
          event.preventDefault();
          handleSeek(Math.max(0, currentTimeRef.current - 15));
          break;
        case 'c':
          event.preventDefault();
          handleSeek(Math.max(0, currentTimeRef.current - 5));
          break;
        case 'v':
          event.preventDefault();
          handleSeek(Math.min(durationRef.current, currentTimeRef.current + 5));
          break;
        case 'b':
          event.preventDefault();
          handleSeek(Math.min(durationRef.current, currentTimeRef.current + 15));
          break;
        case 'n':
          event.preventDefault();
          handleSeek(Math.min(durationRef.current, currentTimeRef.current + 30));
          break;
        case 's':
          event.preventDefault();
          createOrUpdateStartMarker();
          break;
        case 'e':
          event.preventDefault();
          createOrUpdateEndMarker();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      try {
        window.removeEventListener('keydown', handleKeyDown);
      } catch (error) {
        console.warn('Error removing keydown event listener:', error);
      }
    };
  }, []); // Remove dependencies to prevent recreation of event listener
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper functions for snippet operations
  const fetchSnippets = async (): Promise<SnippetData[]> => {
    try {
      const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileId}?t=${Date.now()}`);
      if (!response.ok) {
        console.error('❌ Error fetching snippets:', response.statusText);
        return [];
      }
      const snippets = await response.json();
      console.log('📋 fetchSnippets returned:', snippets);
      return snippets;
    } catch (error) {
      console.error('❌ Error fetching snippets:', error);
      return [];
    }
  };

  const restoreActiveSnippetState = async (snippets: SnippetData[]) => {
    try {
      // Find the active snippet from the database
      const activeSnippet = snippets.find(snippet => snippet.isActive);
      
      if (activeSnippet) {
        console.log('🎬 Restoring active snippet from database:', activeSnippet.name);
        setActiveSnippet(activeSnippet);
        setLoopMode('snippet');
      } else {
        console.log('🎬 No active snippet found in database');
        setActiveSnippet(null);
        // Reset loop tracking when snippet is cleared
        lastLoopTrackedRef.current = { snippetId: null, time: 0 };
        setLoopMode('none');
      }
    } catch (error) {
      console.error('❌ Error restoring active snippet state:', error);
    }
  };

  const persistActiveSnippetState = async (activeSnippet: SnippetData) => {
    try {
      // Check if component is still mounted
      if (!isMountedRef.current) return;
      
      // Update all snippets to set isActive to false
      const updatedSnippets = backendSnippetsRef.current.map(snippet => ({
        ...snippet,
        isActive: snippet.id === activeSnippet.id
      }));
      
      // Update each snippet in the database
      for (const snippet of updatedSnippets) {
        // Check again if component is still mounted before each update
        if (!isMountedRef.current) return;
        await auditioBackendManager.updateSnippet(snippet);
      }
      
      // Check if component is still mounted before updating local state
      if (isMountedRef.current) {
        // Update local state
        setBackendSnippets(updatedSnippets);
      }
    } catch (error) {
      console.error('❌ Error persisting active snippet state:', error);
    }
  };

  // Find the nearest snippet based on current time (reused from audio version)
  const findNearestSnippet = (snippets: SnippetData[], currentTime: number): SnippetData | null => {
    if (!snippets || snippets.length === 0) return null;
    
    // Sort snippets by start time
    const sortedSnippets = [...snippets].sort((a, b) => a.startTime - b.startTime);
    
    // Case 1: Check if current time is between any snippet's start and end time
    const currentSnippet = sortedSnippets.find(snippet => 
      currentTime >= snippet.startTime && currentTime <= snippet.endTime
    );
    
    if (currentSnippet) {
      // If multiple snippets contain current time, pick the one with earlier start time
      const overlappingSnippets = sortedSnippets.filter(snippet => 
        currentTime >= snippet.startTime && currentTime <= snippet.endTime
      );
      return overlappingSnippets[0]; // Already sorted by start time, so first is earliest
    }
    
    // Case 2: Current time is not between any snippet
    // Find the next snippet whose start time is nearest to current time
    const futureSnippets = sortedSnippets.filter(snippet => snippet.startTime > currentTime);
    
    if (futureSnippets.length > 0) {
      // Find the snippet with start time closest to current time
      return futureSnippets.reduce((nearest, current) => 
        Math.abs(current.startTime - currentTime) < Math.abs(nearest.startTime - currentTime) 
          ? current 
          : nearest
      );
    } else {
      // No future snippets, find the nearest snippet before current time
      const pastSnippets = sortedSnippets.filter(snippet => snippet.startTime <= currentTime);
      if (pastSnippets.length > 0) {
        // Return the snippet with the latest start time (closest to current time)
        return pastSnippets[pastSnippets.length - 1];
      }
    }
    
    return null;
  };

  const handleCreateSnippet = async (startTime: number, endTime: number, name: string) => {
    console.log('🎯 handleCreateSnippet called with:', { startTime, endTime, name });
    try {
      // Create a mock region object for the addOrUpdateSnippetFromRegion method
      const mockRegion = {
        id: `snippet_${Date.now()}`,
        start: startTime,
        end: endTime,
        content: name
      };
      console.log('🎯 Creating mock region:', mockRegion);
      await auditioBackendManager.addOrUpdateSnippetFromRegion(mockRegion, audioFileId);
      console.log('✅ Snippet created successfully');
      
      // Wait a moment for the backend to process the creation
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Refresh snippets from backend
      const snippets = await fetchSnippets();
      console.log('📋 Fetched snippets:', snippets);
      setBackendSnippets(snippets);
      onSnippetsChange();
      // Trigger refresh in UnifiedSnippetsView
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('❌ Error creating snippet:', error);
    }
  };

  const handleUpdateSnippet = async (updatedSnippet: SnippetData) => {
    try {
      await auditioBackendManager.updateSnippet(updatedSnippet);
      setBackendSnippets(prev => prev.map(s => 
        s.id === updatedSnippet.id ? updatedSnippet : s
      ));
      onSnippetsChange();
    } catch (error) {
      console.error('Error updating snippet:', error);
    }
  };

  // Extract video ID from YouTube URL
  const extractVideoId = (url: string): string => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(youtubeRegex);
    return match ? match[4] : '';
  };

  const videoId = extractVideoId(youtubeUrl);
  console.log('🎬 Extracted video ID:', videoId, 'from URL:', youtubeUrl);

  // Load snippets on component mount
  useEffect(() => {
    isMountedRef.current = true;
    const loadSnippets = async () => {
      const snippets = await fetchSnippets();
      setBackendSnippets(snippets);
      
      // Restore start marker from snippets if it exists, otherwise clear it
      const startMarkerSnippet = snippets.find(s => s.name === "Start Marker");
      if (startMarkerSnippet) {
        setStartMarker(startMarkerSnippet.startTime);
        console.log('📍 Restored start marker from database:', startMarkerSnippet.startTime);
      } else {
        setStartMarker(null);
        console.log('📍 No start marker snippet found, clearing start marker');
      }
      
      // Restore end marker from snippets if it exists, otherwise clear it
      const endMarkerSnippet = snippets.find(s => s.name === "End Marker");
      if (endMarkerSnippet) {
        setEndMarker(endMarkerSnippet.startTime);
        console.log('📍 Restored end marker from database:', endMarkerSnippet.startTime);
      } else {
        setEndMarker(null);
        console.log('📍 No end marker snippet found, clearing end marker');
      }
      
      // Restore active snippet state from database
      await restoreActiveSnippetState(snippets);
    };
    loadSnippets();
    return () => {
      isMountedRef.current = false;
    };
  }, [audioFileId]);

  // Fetch ownerId when audioFileId changes
  useEffect(() => {
    const fetchOwnerId = async () => {
      if (audioFileId && auditioBackendManager) {
        try {
          const fileData = await auditioBackendManager.getAudioFileById(audioFileId);
          console.log('🔍 YouTubeVideoViewer - Fetched file data:', {
            audioFileId,
            ownerId: fileData?.ownerId,
            ownerIdType: typeof fileData?.ownerId,
            allKeys: fileData ? Object.keys(fileData) : []
          });
          setOwnerId(fileData?.ownerId);
        } catch (error) {
          console.error('Error fetching ownerId:', error);
          setOwnerId(undefined);
        }
      } else {
        setOwnerId(undefined);
      }
    };
    fetchOwnerId();
  }, [audioFileId, auditioBackendManager]);

  const handleSnippetsChange = async () => {
    const snippets = await fetchSnippets();
    setBackendSnippets(snippets);
    
    // Check if start marker snippet still exists, if not clear the marker
    const startMarkerSnippet = snippets.find(s => s.name === "Start Marker");
    if (!startMarkerSnippet && startMarker !== null) {
      console.log('📍 Start marker snippet deleted, clearing start marker');
      setStartMarker(null);
    } else if (startMarkerSnippet && startMarker !== startMarkerSnippet.startTime) {
      // Update start marker if it exists but time changed
      setStartMarker(startMarkerSnippet.startTime);
    }
    
    // Check if end marker snippet still exists, if not clear the marker
    const endMarkerSnippet = snippets.find(s => s.name === "End Marker");
    if (!endMarkerSnippet && endMarker !== null) {
      console.log('📍 End marker snippet deleted, clearing end marker');
      setEndMarker(null);
    } else if (endMarkerSnippet && endMarker !== endMarkerSnippet.startTime) {
      // Update end marker if it exists but time changed
      setEndMarker(endMarkerSnippet.startTime);
    }
    
    // Restore active snippet state after snippets change
    await restoreActiveSnippetState(snippets);
    
    onSnippetsChange();
  };

  const handlePlayerReady = useCallback((player: any) => {
    console.log('🎬 YouTube player ready:', player);
    playerRef.current = player;
    // Get initial duration
    const playerDuration = player.getDuration();
    console.log('🎬 Player duration:', playerDuration);
    if (playerDuration) {
      setDuration(playerDuration);
    }
  }, []);

  const handleStateChange = useCallback((event: any) => {
    // YouTube Player States: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (video cued)
    const state = event.data;
    const isActuallyPlaying = state === 1; // Only state 1 is actually playing
    console.log('🎬 YouTube player state change:', state, 'isActuallyPlaying:', isActuallyPlaying);
    setIsPlaying(isActuallyPlaying);
    
    // Handle track looping when video ends
    if (state === 0 && loopMode === 'track') { // 0 = ended
      console.log('🎬 Video ended, looping track');
      
      // Track track loop event
      const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
      if (audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)) {
        console.log('📊 Tracking track loop for audioFileId:', audioFileIdNum);
        (async () => {
          let idToken: string | null = null;
          if (user && firebaseUser) {
            try {
              idToken = await firebaseUser.getIdToken();
            } catch (error) {
              console.warn('Could not get auth token for metrics:', error);
            }
          }
          metricsService.trackTrackPlay(audioFileIdNum, user?.id || null, idToken);
        })();
      }
      
      setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.seekTo(0);
          playerRef.current.playVideo();
        }
      }, 500); // Slightly longer delay to ensure clean restart
    }
  }, [loopMode]);

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time);
    
    // Reset loop tracking if we've looped back to start (time went back below startTime)
    if (loopMode === 'snippet' && activeSnippet) {
      const snippetIdNum = typeof activeSnippet.id === 'string' ? parseInt(String(activeSnippet.id), 10) : Number(activeSnippet.id);
      // If we're back at or before startTime and we had tracked at endTime, reset for next loop
      if (time <= activeSnippet.startTime && 
          lastLoopTrackedRef.current.snippetId === snippetIdNum && 
          lastLoopTrackedRef.current.time >= activeSnippet.endTime) {
        lastLoopTrackedRef.current = { snippetId: snippetIdNum, time: 0 };
      }
    }
    
    // Check if we need to loop based on loop mode
    if (loopMode === 'snippet' && activeSnippet && time >= activeSnippet.endTime) {
      const snippetIdNum = typeof activeSnippet.id === 'string' ? parseInt(String(activeSnippet.id), 10) : Number(activeSnippet.id);
      
      // Only track loop once per cycle - check if we've already tracked this loop
      const shouldTrack = lastLoopTrackedRef.current.snippetId !== snippetIdNum || 
                         lastLoopTrackedRef.current.time < activeSnippet.endTime;
      
      if (shouldTrack) {
        console.log('🎬 Looping snippet:', activeSnippet.name, 'from', activeSnippet.startTime, 'to', activeSnippet.endTime, 'current time:', time);
        
        // Track snippet loop event
        const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
        
        if (audioFileIdNum && audioFileIdNum > 0 && snippetIdNum && snippetIdNum > 0 && !isNaN(snippetIdNum)) {
          console.log('📊 Tracking snippet loop for audioFileId:', audioFileIdNum, 'snippetId:', snippetIdNum);
          (async () => {
            let idToken: string | null = null;
            if (user && firebaseUser) {
              try {
                idToken = await firebaseUser.getIdToken();
              } catch (error) {
                console.warn('Could not get auth token for metrics:', error);
              }
            }
            metricsService.trackSnippetPlay(audioFileIdNum, snippetIdNum, user?.id || null, idToken);
          })();
          
          // Mark this loop as tracked
          lastLoopTrackedRef.current = { snippetId: snippetIdNum, time: activeSnippet.endTime };
        }
      }
      
      playerRef.current?.seekTo(activeSnippet.startTime);
      playerRef.current?.playVideo();
    } else if (loopMode === 'snippet' && activeSnippet) {
      console.log('🎬 Snippet mode active, snippet:', activeSnippet.name, 'current time:', time, 'snippet end time:', activeSnippet.endTime);
    } else if (loopMode === 'snippet' && !activeSnippet) {
      console.log('🎬 Snippet mode active but no active snippet');
      
      // If snippet mode is active but no snippet is selected, check if current time is within any snippet
      const currentSnippet = backendSnippetsRef.current.find(snippet => 
        time >= snippet.startTime && time <= snippet.endTime
      );
      
      if (currentSnippet) {
        console.log('🎬 Auto-activating snippet at current time:', currentSnippet.name);
        setActiveSnippet(currentSnippet);
        // Reset loop tracking when snippet changes
        const snippetIdNum = typeof currentSnippet.id === 'string' ? parseInt(String(currentSnippet.id), 10) : Number(currentSnippet.id);
        lastLoopTrackedRef.current = { snippetId: snippetIdNum, time: 0 };
        persistActiveSnippetState(currentSnippet);
      }
    }
    // If snippet mode is selected but no active snippet, just continue playing normally
  }, [loopMode, activeSnippet, persistActiveSnippetState, backendSnippetsRef]);

  const handleSeek = useCallback((time: number) => {
    console.log('🎬 Seeking to time:', time);
    
    try {
      // If there's an active snippet with loop mode 'snippet', check if we're clicking outside it
      if (activeSnippet && loopMode === 'snippet') {
        const isOutsideActiveSnippet = time < activeSnippet.startTime || time > activeSnippet.endTime;
        if (isOutsideActiveSnippet) {
          console.log('🎬 Clicked outside active snippet with loop mode snippet - changing to none');
          setLoopMode('none');
          setActiveSnippet(null);
        }
      }
      
      if (playerRef.current) {
        playerRef.current.seekTo(time);
        // Immediately update currentTime state so timeline reflects the change
        setCurrentTime(time);
      }
    } catch (error) {
      console.error('🎬 Error in handleSeek:', error);
    }
  }, [activeSnippet, loopMode]);

  const handlePlayPause = useCallback(() => {
    console.log('🎬 Play/Pause clicked, playerRef:', playerRef.current, 'isPlaying:', isPlaying);
    if (playerRef.current) {
      try {
        // Get the actual player state to double-check
        const playerState = playerRef.current.getPlayerState();
        console.log('🎬 Actual player state:', playerState, 'React state isPlaying:', isPlaying);
        
        // State 1 = playing, State 2 = paused, State 5 = cued (not playing), -1 = unstarted
        const isActuallyPlaying = playerState === 1;
        
        if (isActuallyPlaying) {
          console.log('🎬 Pausing video (actual state is playing)');
          playerRef.current.pauseVideo();
        } else if (playerState === 2 || playerState === 5) {
          // Only play if player is paused or cued
          console.log('🎬 Playing video (actual state is paused/cued)');
          // Add a small delay to ensure the player is ready
          setTimeout(async () => {
            try {
              if (playerRef.current) {
                // If there's an active snippet and loop mode is enabled, start from snippet start time
                if (activeSnippet && (loopMode === 'snippet' || loopMode === 'track')) {
                  console.log('🎬 Starting playback from active snippet:', activeSnippet.name, 'at time:', activeSnippet.startTime);
                  playerRef.current.seekTo(activeSnippet.startTime);
                  setCurrentTime(activeSnippet.startTime);
                }
                playerRef.current.playVideo();
                // Track playback event (non-blocking)
                // Check if snippet mode is active - if so, track snippet_play, otherwise track_play
                const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
                
                console.log('📊 YouTubeVideoViewer - Play event tracking check:', {
                  loopMode,
                  hasActiveSnippet: !!activeSnippet,
                  activeSnippetId: activeSnippet?.id,
                  audioFileIdNum
                });
                
                // Track snippet_play if snippet mode is active AND there's an active snippet
                if (loopMode === 'snippet' && activeSnippet && audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)) {
                  const snippetIdNum = typeof activeSnippet.id === 'string' ? parseInt(String(activeSnippet.id), 10) : Number(activeSnippet.id);
                  
                  if (snippetIdNum && snippetIdNum > 0 && !isNaN(snippetIdNum)) {
                    console.log('📊 Tracking snippet playback (YouTube) for audioFileId:', audioFileIdNum, 'snippetId:', snippetIdNum);
                    (async () => {
                      let idToken: string | null = null;
                      if (user && firebaseUser) {
                        try {
                          idToken = await firebaseUser.getIdToken();
                        } catch (error) {
                          console.warn('Could not get auth token for metrics:', error);
                        }
                      }
                      metricsService.trackSnippetPlay(audioFileIdNum, snippetIdNum, user?.id || null, idToken);
                    })();
                  } else {
                    console.warn('⚠️ Snippet mode active but invalid snippetId:', snippetIdNum);
                    // Fall through to track_play
                  }
                } else if (audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)) {
                  // Track track_play if not in snippet mode, or snippet mode but no active snippet
                  console.log('📊 Tracking track playback (YouTube) for audioFileId:', audioFileIdNum, 'reason:', loopMode === 'snippet' ? 'snippet mode but no active snippet' : 'not in snippet mode');
                  let idToken: string | null = null;
                  if (user && firebaseUser) {
                      try {
                          idToken = await firebaseUser.getIdToken();
                      } catch (error) {
                          // Silently fail - tracking works without token
                          console.warn('Could not get auth token for metrics:', error);
                      }
                  }
                  metricsService.trackTrackPlay(audioFileIdNum, user?.id || null, idToken);
                } else {
                  console.warn('⚠️ Skipping playback tracking - invalid audioFileId:', audioFileId, 'converted:', audioFileIdNum);
                }
              }
            } catch (error) {
              console.error('🎬 Error in play operations:', error);
            }
          }, 100);
        } else {
          console.log('🎬 Player not in valid state for play/pause:', playerState);
        }
      } catch (error) {
        console.log('🎬 Error getting player state:', error);
        // Fallback to React state logic only if we can't get player state
        try {
          if (isPlaying) {
            console.log('🎬 Pausing video (fallback)');
            playerRef.current.pauseVideo();
          } else {
            console.log('🎬 Playing video (fallback)');
            setTimeout(async () => {
              try {
                if (playerRef.current) {
                  // If there's an active snippet and loop mode is enabled, start from snippet start time
                  if (activeSnippet && (loopMode === 'snippet' || loopMode === 'track')) {
                    console.log('🎬 Starting playback from active snippet (fallback):', activeSnippet.name, 'at time:', activeSnippet.startTime);
                    playerRef.current.seekTo(activeSnippet.startTime);
                    setCurrentTime(activeSnippet.startTime);
                  }
                  playerRef.current.playVideo();
                  // Track playback event (non-blocking)
                  // Check if snippet mode is active - if so, track snippet_play, otherwise track_play
                  const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
                  
                  console.log('📊 YouTubeVideoViewer - Fallback play event tracking check:', {
                    loopMode,
                    hasActiveSnippet: !!activeSnippet,
                    activeSnippetId: activeSnippet?.id,
                    audioFileIdNum
                  });
                  
                  // Track snippet_play if snippet mode is active AND there's an active snippet
                  if (loopMode === 'snippet' && activeSnippet && audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)) {
                    const snippetIdNum = typeof activeSnippet.id === 'string' ? parseInt(String(activeSnippet.id), 10) : Number(activeSnippet.id);
                    
                    if (snippetIdNum && snippetIdNum > 0 && !isNaN(snippetIdNum)) {
                      console.log('📊 Tracking snippet playback (YouTube fallback) for audioFileId:', audioFileIdNum, 'snippetId:', snippetIdNum);
                      (async () => {
                        let idToken: string | null = null;
                        if (user && firebaseUser) {
                          try {
                            idToken = await firebaseUser.getIdToken();
                          } catch (error) {
                            console.warn('Could not get auth token for metrics:', error);
                          }
                        }
                        metricsService.trackSnippetPlay(audioFileIdNum, snippetIdNum, user?.id || null, idToken);
                      })();
                    } else {
                      console.warn('⚠️ Snippet mode active but invalid snippetId:', snippetIdNum);
                      // Fall through to track_play
                    }
                  } else if (audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)) {
                    // Track track_play if not in snippet mode, or snippet mode but no active snippet
                    console.log('📊 Tracking track playback (YouTube fallback) for audioFileId:', audioFileIdNum, 'reason:', loopMode === 'snippet' ? 'snippet mode but no active snippet' : 'not in snippet mode');
                    let idToken: string | null = null;
                    if (user && firebaseUser) {
                      try {
                          idToken = await firebaseUser.getIdToken();
                      } catch (error) {
                          // Silently fail - tracking works without token
                          console.warn('Could not get auth token for metrics:', error);
                      }
                    }
                    metricsService.trackTrackPlay(audioFileIdNum, user?.id || null, idToken);
                  } else {
                    console.warn('⚠️ Skipping playback tracking - invalid audioFileId:', audioFileId, 'converted:', audioFileIdNum);
                  }
                }
              } catch (error) {
                console.error('🎬 Error in fallback play operations:', error);
              }
            }, 100);
          }
        } catch (error) {
          console.error('🎬 Error in fallback logic:', error);
        }
      }
    } else {
      console.log('🎬 Player not ready yet');
    }
  }, [isPlaying, activeSnippet, loopMode]);

  const handleSnippetClick = useCallback(async (snippet: SnippetData) => {
    console.log('🎯 Snippet clicked:', snippet.name);
    
    try {
      // Set the clicked snippet as active
      setActiveSnippet(snippet);
      
      // Set loop mode to 'snippet' when clicking on a snippet
      setLoopMode('snippet');
      console.log('🎯 Setting loop mode to snippet and starting playback from:', snippet.name);
      
      // Persist the active snippet state to the database
      await persistActiveSnippetState(snippet);
      
      // Track snippet playback event (non-blocking)
      // Convert IDs to numbers (they might be strings)
      const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
      const snippetIdNum = typeof snippet.id === 'string' ? parseInt(String(snippet.id), 10) : Number(snippet.id);
      
      // Reset loop tracking when snippet changes
      lastLoopTrackedRef.current = { snippetId: snippetIdNum, time: 0 };
      
      if (audioFileIdNum && audioFileIdNum > 0 && snippetIdNum && snippetIdNum > 0 && !isNaN(snippetIdNum)) {
        console.log('📊 Tracking snippet playback for audioFileId:', audioFileIdNum, 'snippetId:', snippetIdNum);
        let idToken: string | null = null;
        if (user && firebaseUser) {
          try {
              idToken = await firebaseUser.getIdToken();
          } catch (error) {
              // Silently fail - tracking works without token
              console.warn('Could not get auth token for metrics:', error);
          }
        }
        metricsService.trackSnippetPlay(audioFileIdNum, snippetIdNum, user?.id || null, idToken);
      } else {
        console.warn('⚠️ Skipping snippet playback tracking - invalid IDs:', { 
          audioFileId, 
          audioFileIdNum,
          snippetId: snippet.id,
          snippetIdNum
        });
      }
      
      // Seek to the snippet start time and start playing
      setTimeout(() => {
        try {
          if (playerRef.current) {
            // Check if player is ready before attempting operations
            const playerState = playerRef.current.getPlayerState();
            console.log('🎯 Player state before snippet operations:', playerState);
            
            // Seek to snippet start time regardless of player state
            playerRef.current.seekTo(snippet.startTime);
            setCurrentTime(snippet.startTime);
            
            // Only play if player is in a valid state and not already playing
            if (playerState !== -1 && playerState !== 1) {
              playerRef.current.playVideo();
            }
          }
        } catch (error) {
          console.error('🎯 Error in player operations:', error);
        }
      }, 100);
    } catch (error) {
      console.error('🎯 Error in handleSnippetClick:', error);
    }
  }, [persistActiveSnippetState]);

  const createOrUpdateStartMarker = useCallback(async () => {
    const currentTimeLocal = currentTimeRef.current;
    
    if (startMarker !== null) {
      // Update existing start marker
      setStartMarker(currentTimeLocal);
      console.log('📍 Updated start marker at:', currentTimeLocal);
      
      // Update the snippet in the database
      try {
        const mockRegion = {
          id: `start_marker_${audioFileId}`,
          start: currentTimeLocal,
          end: currentTimeLocal, // Start marker is a point, so start and end are the same
          content: "Start Marker"
        };
        await auditioBackendManager.addOrUpdateSnippetFromRegion(mockRegion, audioFileId);
        console.log('✅ Start marker snippet updated in database');
        
        // Refresh snippets from backend
        const snippets = await fetchSnippets();
        setBackendSnippets(snippets);
        onSnippetsChange();
        // Trigger refresh in UnifiedSnippetsView
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        console.error('❌ Error updating start marker snippet:', error);
      }
    } else {
      // Create new start marker
      setStartMarker(currentTimeLocal);
      console.log('📍 Created new start marker at:', currentTimeLocal);
      
      // Save the start marker as a snippet in the database
      try {
        const mockRegion = {
          id: `start_marker_${audioFileId}`,
          start: currentTimeLocal,
          end: currentTimeLocal, // Start marker is a point, so start and end are the same
          content: "Start Marker"
        };
        await auditioBackendManager.addOrUpdateSnippetFromRegion(mockRegion, audioFileId);
        console.log('✅ Start marker snippet saved to database');
        
        // Wait a moment for the backend to process the creation
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Refresh snippets from backend
        const snippets = await fetchSnippets();
        setBackendSnippets(snippets);
        onSnippetsChange();
        // Trigger refresh in UnifiedSnippetsView
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        console.error('❌ Error saving start marker snippet:', error);
      }
    }
  }, [startMarker, audioFileId, auditioBackendManager, fetchSnippets, onSnippetsChange]);

  const createOrUpdateEndMarker = useCallback(async () => {
    const currentTimeLocal = currentTimeRef.current;
    
    if (endMarker !== null) {
      // Update existing end marker
      setEndMarker(currentTimeLocal);
      console.log('📍 Updated end marker at:', currentTimeLocal);
      
      // Update the snippet in the database
      try {
        const mockRegion = {
          id: `end_marker_${audioFileId}`,
          start: currentTimeLocal,
          end: currentTimeLocal, // End marker is a point, so start and end are the same
          content: "End Marker"
        };
        await auditioBackendManager.addOrUpdateSnippetFromRegion(mockRegion, audioFileId);
        console.log('✅ End marker snippet updated in database');
        
        // Refresh snippets from backend
        const snippets = await fetchSnippets();
        setBackendSnippets(snippets);
        onSnippetsChange();
        // Trigger refresh in UnifiedSnippetsView
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        console.error('❌ Error updating end marker snippet:', error);
      }
    } else {
      // Create new end marker
      setEndMarker(currentTimeLocal);
      console.log('📍 Created new end marker at:', currentTimeLocal);
      
      // Save the end marker as a snippet in the database
      try {
        const mockRegion = {
          id: `end_marker_${audioFileId}`,
          start: currentTimeLocal,
          end: currentTimeLocal, // End marker is a point, so start and end are the same
          content: "End Marker"
        };
        await auditioBackendManager.addOrUpdateSnippetFromRegion(mockRegion, audioFileId);
        console.log('✅ End marker snippet saved to database');
        
        // Wait a moment for the backend to process the creation
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Refresh snippets from backend
        const snippets = await fetchSnippets();
        setBackendSnippets(snippets);
        onSnippetsChange();
        // Trigger refresh in UnifiedSnippetsView
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        console.error('❌ Error saving end marker snippet:', error);
      }
    }
    
    // Automatically create snippet if both start and end markers exist
    setTimeout(async () => {
      // Use refs to get the current values
      const currentStartMarker = startMarkerRef.current;
      const currentEndMarker = currentTimeLocal;
      
      console.log('🔍 Checking markers for snippet creation:', { currentStartMarker, currentEndMarker });
      
      if (currentStartMarker !== null && currentEndMarker !== null) {
        console.log('✅ Both markers exist, creating snippet');
        
        // Create snippet from start and end markers (use min/max to handle order)
        const startTime = Math.min(currentStartMarker, currentEndMarker);
        const endTime = Math.max(currentStartMarker, currentEndMarker);
        
        try {
          await handleCreateSnippet(startTime, endTime, `Snippet ${startTime.toFixed(1)}s - ${endTime.toFixed(1)}s`);
          console.log('✅ Snippet created successfully');
          
          // Delete the start and end marker snippets from the database
          try {
            // Delete start marker snippet
            const startMarkerSnippet = backendSnippetsRef.current.find(s => s.name === "Start Marker");
            if (startMarkerSnippet) {
              const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets`, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  audioFileId: audioFileId,
                  regionId: startMarkerSnippet.regionId,
                }),
              });
              if (!response.ok) {
                console.warn('⚠️ Failed to delete start marker snippet:', response.statusText);
              }
            }
            
            // Delete end marker snippet
            const endMarkerSnippet = backendSnippetsRef.current.find(s => s.name === "End Marker");
            if (endMarkerSnippet) {
              const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets`, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  audioFileId: audioFileId,
                  regionId: endMarkerSnippet.regionId,
                }),
              });
              if (!response.ok) {
                console.warn('⚠️ Failed to delete end marker snippet:', response.statusText);
              }
            }
          } catch (error) {
            console.warn('⚠️ Error deleting marker snippets:', error);
          }
          
          // Clear both markers after creating the snippet
          setStartMarker(null);
          setEndMarker(null);
          
          // Wait a moment for the backend to process the creation
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Refresh snippets list
          const updatedSnippets = await fetchSnippets();
          setBackendSnippets(updatedSnippets);
          onSnippetsChange();
          // Trigger refresh in UnifiedSnippetsView
          setRefreshTrigger(prev => prev + 1);
        } catch (error) {
          console.error('❌ Error creating snippet:', error);
        }
      } else {
        console.log('⏳ Waiting for both markers to be set');
      }
    }, 100);
  }, [endMarker, audioFileId, auditioBackendManager, handleCreateSnippet, fetchSnippets, onSnippetsChange, backendSnippetsRef]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFlipToggle = () => {
    setIsFlipped(!isFlipped);
  };

  // Handle playback rate changes
  const handlePlaybackRateChange = (newRate: number) => {
    setPlaybackRate(newRate);
    if (playerRef.current && playerRef.current.setPlaybackRate) {
      playerRef.current.setPlaybackRate(newRate);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      {/* Player Container with Dark Background */}
      <Box
        className="youtube-player-full-container"
        sx={{
          position: 'relative',
          backgroundColor: '#3a4f61',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '1rem',
          width: '100%',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(2px 2px at 20px 30px, #ffffff, transparent),
              radial-gradient(2px 2px at 40px 70px, #ffffff, transparent),
              radial-gradient(1px 1px at 90px 40px, #ffffff, transparent),
              radial-gradient(1px 1px at 130px 80px, #ffffff, transparent),
              radial-gradient(2px 2px at 160px 30px, #ffffff, transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px 100px',
            opacity: 0.3,
            borderRadius: '12px',
            pointerEvents: 'none'
          }
        }}
      >
        {/* YouTube Player */}
        <Box 
          className="youtube-player-container" 
          sx={{ 
            mb: 3, 
            width: '100%'
          }}
        >
          <YouTubePlayer
            videoId={videoId}
            onReady={handlePlayerReady}
            onStateChange={handleStateChange}
            onTimeUpdate={handleTimeUpdate}
            onSeek={handleSeek}
            width="100%"
            height={360}
            isFlipped={isFlipped}
            playbackRate={playbackRate}
          />
        </Box>

        {/* Timeline */}
        <Box 
          ref={timelineContainerRef}
          sx={{ mb: 2, position: 'relative', zIndex: 1, width: '100%' }}
        >
          <YouTubeTimeline
            currentTime={currentTime}
            duration={duration}
            snippets={backendSnippetsRef.current.map(snippet => ({
              ...snippet,
              isActive: activeSnippet?.id === snippet.id
            }))}
            onSeek={handleSeek}
            onSnippetClick={handleSnippetClick}
            width="100%"
            startMarker={startMarker}
            endMarker={endMarker}
            zoomLevel={zoomLevel}
            onZoomChange={setZoomLevel}
            showZoomControls={false}
          />
        </Box>
        
        {/* Control Row: Playback Speed (left), Flip (center), Zoom (right) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            width: '100%',
            px: 1
          }}
        >
          {/* Playback Speed - Left aligned */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PlaybackSpeedDropdown
              playbackRate={playbackRate}
              onPlaybackRateChange={handlePlaybackRateChange}
            />
          </Box>
          
          {/* Flip - Center aligned */}
          <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
            <Tooltip title={isFlipped ? "Flip Back" : "Flip Video"}>
              <button 
                className="speed-control"
                onClick={handleFlipToggle}
                style={{
                  backgroundColor: isFlipped ? 'rgba(85, 203, 205, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(85, 203, 205, 0.2)',
                  borderRadius: '16px',
                  padding: '0.3rem 0.7rem',
                  color: 'white',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isFlipped ? 1 : 0.7,
                  boxShadow: isFlipped ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                🔄 Flip
              </button>
            </Tooltip>
          </Box>
          
          {/* Zoom Controls - Right aligned */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Zoom Out">
              <IconButton
                size="small"
                onClick={() => {
                  const currentViewportWidth = timelineContainerRef.current?.clientWidth || viewportWidth;
                  if (duration > 0 && currentViewportWidth > 0) {
                    const minZoom = currentViewportWidth / duration; // Fit to screen (100% view)
                    const maxZoom = currentViewportWidth / 0.1; // Max: 0.1 seconds per pixel
                    const newZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel / 1.5));
                    setZoomLevel(newZoom);
                  }
                }}
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 1)',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <ZoomOut fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom In">
              <IconButton
                size="small"
                onClick={() => {
                  const currentViewportWidth = timelineContainerRef.current?.clientWidth || viewportWidth;
                  if (duration > 0 && currentViewportWidth > 0) {
                    const minZoom = currentViewportWidth / duration; // Fit to screen (100% view)
                    const maxZoom = currentViewportWidth / 0.1; // Max: 0.1 seconds per pixel
                    const newZoom = Math.min(maxZoom, Math.max(minZoom, zoomLevel * 1.5));
                    setZoomLevel(newZoom);
                  }
                }}
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 1)',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Controls - moved below timeline to match audio version */}
        <Box 
          className="media-controls-row"
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'center', md: 'space-between' },
            alignItems: 'center', 
            mb: 2, 
            position: 'relative', 
            zIndex: 1,
            gap: { xs: '1rem', md: '0.5rem' },
            textAlign: { xs: 'center', md: 'left' },
            width: '100%'
          }}
        >
          {/* Time Jump Controls - Left side on desktop, top on mobile */}
          <Box 
            className="time-jump-controls"
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5rem',
              width: { xs: '100%', md: 'auto' }
            }}
          >
            <button className="time-jump-btn backward" onClick={() => handleSeek(Math.max(0, currentTimeRef.current - 30))}>
              <FastRewind />
              30s
            </button>
            <button className="time-jump-btn backward" onClick={() => handleSeek(Math.max(0, currentTimeRef.current - 10))}>
              <FastRewind />
              10s
            </button>
            <button className="time-jump-btn backward" onClick={() => handleSeek(Math.max(0, currentTimeRef.current - 5))}>
              <FastRewind />
              5s
            </button>
            <button className="time-jump-btn forward" onClick={() => handleSeek(Math.min(durationRef.current, currentTimeRef.current + 5))}>
              <FastForward />
              5s
            </button>
            <button className="time-jump-btn forward" onClick={() => handleSeek(Math.min(durationRef.current, currentTimeRef.current + 10))}>
              <FastForward />
              10s
            </button>
            <button className="time-jump-btn forward" onClick={() => handleSeek(Math.min(durationRef.current, currentTimeRef.current + 30))}>
              <FastForward />
              30s
            </button>
          </Box>

          {/* Play Button - Center on desktop, middle on mobile */}
          <Box 
            className="play-button-container"
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              width: { xs: '100%', md: 'auto' },
              position: { xs: 'static', md: 'absolute' },
              left: { xs: 'auto', md: '50%' },
              transform: { xs: 'none', md: 'translateX(-50%)' }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              {isOwner && (
                <Tooltip title="Start Marker">
                  <button 
                    className="speed-control"
                    id="start-marker-btn"
                    onClick={createOrUpdateStartMarker}
                  >
                    Start
                  </button>
                </Tooltip>
              )}
              
              <Tooltip title="Play">
                <div className="play-button-container">
                  <button 
                    className="play-btn"
                    onClick={handlePlayPause}
                  > 
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </button>
                </div>
              </Tooltip>
              
              {isOwner && (
                <Tooltip title="End Marker">
                  <button 
                    className="speed-control"
                    id="end-marker-btn"
                    onClick={createOrUpdateEndMarker}
                  >
                    End
                  </button>
                </Tooltip>
              )}
              
            </Box>
          </Box>

          {/* Loop Controls - Right side on desktop, bottom on mobile */}
          <Box 
            className="loop-controls"
            sx={{ 
              display: 'flex', 
              justifyContent: { xs: 'center', md: 'flex-end' },
              alignItems: 'center',
              width: { xs: '100%', md: 'auto' },
              flexDirection: 'row',
              gap: 2
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                fontSize: '0.8rem',
                opacity: 0.8,
                mr: 1
              }}
            >
              Loop:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: '3px',
                border: '1px solid rgba(85, 203, 205, 0.2)'
              }}
            >
              <Box
                sx={{
                  padding: '0.3rem 0.7rem',
                  borderRadius: '16px',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: 'white',
                  opacity: loopMode === 'none' ? 1 : 0.7,
                  backgroundColor: loopMode === 'none' ? 'rgba(85, 203, 205, 0.3)' : 'transparent',
                  boxShadow: loopMode === 'none' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                  '&:hover': {
                    opacity: loopMode === 'none' ? 1 : 0.9
                  }
                }}
                onClick={() => setLoopMode('none')}
              >
                None
              </Box>
              <Box
                sx={{
                  padding: '0.3rem 0.7rem',
                  borderRadius: '16px',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: 'white',
                  opacity: loopMode === 'snippet' ? 1 : 0.7,
                  backgroundColor: loopMode === 'snippet' ? 'rgba(85, 203, 205, 0.3)' : 'transparent',
                  boxShadow: loopMode === 'snippet' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                  '&:hover': {
                    opacity: loopMode === 'snippet' ? 1 : 0.9
                  }
                }}
                onClick={async () => {
                  // If no active snippet, find and activate the nearest one
                  if (!activeSnippet) {
                    const nearestSnippet = findNearestSnippet(backendSnippetsRef.current, currentTimeRef.current);
                    if (nearestSnippet) {
                      console.log('🎬 No active snippet, activating nearest snippet:', nearestSnippet.name);
                      setActiveSnippet(nearestSnippet);
                      // Reset loop tracking when snippet changes
                      const snippetIdNum = typeof nearestSnippet.id === 'string' ? parseInt(String(nearestSnippet.id), 10) : Number(nearestSnippet.id);
                      lastLoopTrackedRef.current = { snippetId: snippetIdNum, time: 0 };
                      await persistActiveSnippetState(nearestSnippet);
                      
                      // Seek to the beginning of the snippet
                      if (playerRef.current) {
                        playerRef.current.seekTo(nearestSnippet.startTime);
                        setCurrentTime(nearestSnippet.startTime);
                      }
                    } else {
                      console.log('🎬 No snippets available for loop mode');
                      return; // Don't set loop mode if no snippets exist
                    }
                  }
                  
                  setLoopMode('snippet');
                }}
              >
                Snippet
              </Box>
              <Box
                sx={{
                  padding: '0.3rem 0.7rem',
                  borderRadius: '16px',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: 'white',
                  opacity: loopMode === 'track' ? 1 : 0.7,
                  backgroundColor: loopMode === 'track' ? 'rgba(85, 203, 205, 0.3)' : 'transparent',
                  boxShadow: loopMode === 'track' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                  '&:hover': {
                    opacity: loopMode === 'track' ? 1 : 0.9
                  }
                }}
                onClick={() => setLoopMode('track')}
              >
                Track
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Snippets Management */}
      <UnifiedSnippetsView
        auditioBackendManager={auditioBackendManager}
        audioFileId={audioFileId}
        onSeek={handleSeek}
        onSnippetsChange={async () => {
          await handleSnippetsChange();
          // Force refresh of UnifiedSnippetsView
          setRefreshTrigger(prev => prev + 1);
        }}
        onSnippetActivate={async (snippet) => {
          console.log('🎬 Activating snippet from table:', snippet.name);
          setActiveSnippet(snippet);
          // Reset loop tracking when snippet changes
          const snippetIdNum = typeof snippet.id === 'string' ? parseInt(String(snippet.id), 10) : Number(snippet.id);
          lastLoopTrackedRef.current = { snippetId: snippetIdNum, time: 0 };
          setLoopMode('snippet');
          
          // Persist the active snippet state to the database
          await persistActiveSnippetState(snippet);
          
          // Seek to the snippet start time
          setTimeout(() => {
            try {
              if (playerRef.current) {
                console.log('🎬 Seeking to snippet start time from table:', snippet.startTime);
                playerRef.current.seekTo(snippet.startTime);
                setCurrentTime(snippet.startTime);
              }
            } catch (error) {
              console.error('🎬 Error seeking to snippet from table:', error);
            }
          }, 100);
        }}
        onSnippetPlayFromHere={async (snippet) => {
          console.log('🎬 Playing snippet from here (no loop):', snippet.name);
          setActiveSnippet(snippet);
          // Reset loop tracking when snippet changes
          const snippetIdNum = typeof snippet.id === 'string' ? parseInt(String(snippet.id), 10) : Number(snippet.id);
          lastLoopTrackedRef.current = { snippetId: snippetIdNum, time: 0 };
          setLoopMode('none');
          
          // Persist the active snippet state to the database
          await persistActiveSnippetState(snippet);
          
          // Seek to the snippet start time and start playing
          setTimeout(() => {
            try {
              if (playerRef.current) {
                console.log('🎬 Seeking to snippet start time from table (play from here):', snippet.startTime);
                playerRef.current.seekTo(snippet.startTime);
                setCurrentTime(snippet.startTime);
                playerRef.current.playVideo();
              }
            } catch (error) {
              console.error('🎬 Error seeking to snippet from table (play from here):', error);
            }
          }, 100);
        }}
        onPlay={() => {
          console.log('🎬 Playing video from snippet table');
          if (playerRef.current) {
            playerRef.current.playVideo();
          }
        }}
        activeSnippet={activeSnippet}
        renderTrigger={refreshTrigger}
        setRenderTrigger={setRefreshTrigger}
        mode="youtube"
        ownerId={ownerId}
      />
    </Box>
  );
};

export default YouTubeVideoViewer; 